import React from "react";
import { LayerProps, LngLat } from "react-map-gl";
import {
  ItaAniaWindLevelLayer,
  landslideLayer,
  rainfallLevelLayer,
  heatmapLayer,
  cocoLayerSeismicCCT,
  cocoLayerFloodCCA,
  cocoLayerLandslideCCF,
  proCivileLayer,
  environmentDistanceFacilityLayer,
  crimeDatasetsSweLayer,
  nasaPrecipitationColorsLayer,
  nasaLandslideColorsLayer,
} from "../policy/policyMapStyle";

import {
  genericRasterLayer,
  landCoverWrlTileUrl,
  landCoverGenericTypeDescription,
  CLMS_HRL_CropTypes_S2023_R10m_json,
  CLMS_HRL_ForestTypes_S2023_R10m_json,
  worldCoverWrlTileUrl,
  worldCoverTypeDescription,
  ESA_WorldCover_2021_v2_json,
} from "../wms/landCoverWms";

import {
  getAniaMap,
  getCoCoZippedGeoJson,
  getProCivileTodayMap,
  getProCivileTomorrowMap,
  getCrimeDataMapSwe,
  getNasaCastPrecipitationItaGeoJson,
  getNasaCastLandslideItaGeoJson,
} from "../map/ZippedGeoJsonReader";
import { ANIA_ENUM } from "../util/AniaEnums";
import {
  createExplosionRiskCircles,
  explosionLayer,
  explosionCentersLayer,
  createExplosionRiskCenters,
} from "./utils.js";
import {
  getGlobalLandslideNowcastGeoJsonUrlForItaly,
  getRainfallOneDayGeoJSONUrlForItaly,
  getRainfallSevenDayGeoJSONUrlForItaly,
} from "../../lib/getRainfallData";
import { getEarthquakeMagnitudeForItaly } from "../../lib/getEarthquakeMagnitudeData";
import {
  getEnvironmentDistanceFireStationData,
  getEnvironmentDistanceSchoolData,
} from "../../lib/getEnvironmentDistanceFacilityData";
import { HazardIdEnum } from "./hazardIdEnum";

import { SeismicClassificationITALegend } from "../legends/seismic/SeismicClassificationITALegend";
import CoastalFloodLegend from "../legends/CoastalFloodLegend";
import CoastalFloodEULegend from "../legends/CoastalFloodEULegend";
import InlandFloodEULegend from "../legends/InlandFloodEULegend";
import { riskEvaluationMapStyles } from "../map/Constants";
import FlashFlood3hEULegend from "../legends/FlashFlood3hEULegend";
import FlashFlood24hEULegend from "../legends/FlashFlood24hEULegend";
import FlashFlood48hEULegend from "../legends/FlashFlood48hEULegend";
import FlashFlood72hEULegend from "../legends/FlashFlood72hEULegend";
import LandslideLegend from "../legends/landslide/LandslideLegend";
import { LandslideNatcatRiskLegend } from "../legends/landslide/LandslideNatcatRiskLegend";
import { InlandFloodIspraRiskLegend } from "../legends/InlandFloodIspraRiskLegend";
import { InlandFloodNatcatLegend } from "../legends/inlandFlood/InlandFloodNatcatLegend";
import { InlandFloodHowden58Legend } from "../legends/inlandFlood/InlandFloodHowden58Legend";
import { SeismicNatcatLegend } from "../legends/seismic/SeismicNatcatLegend";
import LightningStrikeLegend from "../legends/LightningStrikeLegend";
import { Snow5CMLegend } from "../legends/Snow5CMLegend";
import Snow25CMLegend from "../legends/Snow25CMLegend";
import HighWindExtremeITALegend from "../legends/HighWindExtremeITALegend";
import HighWindSevereITALegend from "../legends/HighWindSeverelTALegend";
import HailITALargeLegend from "../legends/HailITALargeLegend";
import HailITAEXLargeLegend from "../legends/HailITAEXLargeLegend";
import EarthquakeMagnitudeLegend from "../legends/EarthquakeMagnitudeLegend";
import { WindyRmaLegend } from "../legends/WindyRmaLegend";
import { HailsRmaLegend } from "../legends/HailsRmaLegend";
import { ItaAniaWindLegend } from "../legends/ItaAniaWindLegend";
import ExplosionLegend from "../legends/ExplosionLegend";
import Rainfall1daysLegend from "../legends/Rainfall1daysLegend";
import Rainfall7daysLegend from "../legends/Rainfall7daysLegend";
import { CocoLegend } from "../legends/CocoLegend";
import { ProtezioneCivileLegend } from "../legends/ProtezioneCivileLegend";
import { SeismicIslNatcatLegend } from "../legends/seismic/SeismicIslNatcatLegend";
import { StormSurgeUSLegend } from "../legends/us/StormSurgeUSLegend";
import { WildfireVHPIndexUsLegend } from "../legends/us/WildfireVHPIndexUSLegend";
import { InlandFloodHowden56Legend } from "../legends/inlandFlood/InlandFloodHowden56Legend";
import { WmsGetLegendGraphic } from "../legends/WmsGetLegendGraphic";

//type Legend = React.ComponentType;
import EnvironmentDistanceFireStationLegend from "../legends/EnvironmentDistanceFireStationLegend";
import EnvironmentDistanceSchoolLegend from "../legends/EnvironmentDistanceSchoolLegend";

import { DigitalElevationModelLegend } from "../legends/DigitalElevationModelLegend";
import { HeightAboveNearestDrainageLegend } from "../legends/HeightAboveNearestDrainageLegend";
import { PopulationDensityEurLegend } from "../legends/PopulationDensityEurLegend";
import { PopulationDensityWorldLegend } from "../legends/PopulationDensityWorldLegend";
import { PopulationDensityUsaLegend } from "../legends/PopulationDensityUsaLegend";
import { DistanceToTheNearestCoastLegend } from "../legends/DistanceToTheNearestCoastLegend";
import { CrimeDatasetSweLegend } from "../legends/CrimeDatasetSweLegend";
import { NasaNowcastPrecipitationLegend } from "../legends/NasaNowcastPrecipitationLegend";
import { NasaNowcastLandslideLegend } from "../legends/NasaNowcastLandslideLegend";

export type HazardMapConfigType = {
  sourceLayer?: LayerProps;
  sourceLayerSecondary?: LayerProps;
  mapStyle?: string;
  // function returns GeoJsonData or null
  getSourceData?: () => Promise<GeoJsonData> | Promise<string>;
  getSourceDataSecondary?: () => Promise<GeoJsonData>;

  sourceTileUrl?: string; // for raster

  legend?: React.ComponentType;
  // these two are used just for EnvironmentDistanceFireStation
  getSourceDataWithParams?: ({ lat, lng }: LngLat) => Promise<GeoJsonData>;
  legendWithParams?: (props: { lat: number; lng: number }) => React.ReactElement;
};

const createLegendComponent = (name: Function, specialProps: any = {}): React.FC<any> => {
  const LegendComponent = name as React.ComponentType;

  const WrappedLegendComponent: React.FC<any> = (props) => {
    const combinedProps = { ...specialProps, ...props };

    return <LegendComponent {...combinedProps} />;
  };

  return WrappedLegendComponent;
};

const makePairOfCoCoHazards = (
  hazardEnumIta: HazardIdEnum,
  hazardEnumRma: HazardIdEnum,
  layer: LayerProps,
  hazardName: string,
  version: string,
): Partial<Record<HazardIdEnum, HazardMapConfigType>> => {
  return {
    [hazardEnumIta]: {
      sourceLayer: layer,
      getSourceData: () => getCoCoZippedGeoJson(hazardEnumIta),
      legend: createLegendComponent(CocoLegend, {
        hazardName,
        companyName: "ITA",
        version,
      }),
    },

    [hazardEnumRma]: {
      sourceLayer: layer,
      getSourceData: () => getCoCoZippedGeoJson(hazardEnumRma),
      legend: createLegendComponent(CocoLegend, {
        hazardName,
        companyName: "RMA",
        version,
      }),
    },
  };
};

export const HazardMapConfig: Partial<Record<HazardIdEnum, HazardMapConfigType>> = {
  [HazardIdEnum.SeismicRiskHowden]: {
    mapStyle: riskEvaluationMapStyles.seismicNatcatRiskStyle,
    legend: createLegendComponent(SeismicNatcatLegend, { howdenVersion: 1 }),
  },
  [HazardIdEnum.InlandFloodHowden]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodNatcatRiskStyle,
    legend: createLegendComponent(InlandFloodNatcatLegend),
  },
  [HazardIdEnum.InlandFloodHowden58]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodNatcat58RiskStyle,
    legend: createLegendComponent(InlandFloodHowden58Legend),
  },
  [HazardIdEnum.InlandFloodHowden56]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodNatcat56RiskStyle,
    legend: createLegendComponent(InlandFloodHowden56Legend),
  },
  [HazardIdEnum.LandslideRiskIspra]: {
    mapStyle: riskEvaluationMapStyles.landSlideIspraMapboxStyle,
    legend: createLegendComponent(LandslideNatcatRiskLegend),
  },
  [HazardIdEnum.InlandFloodIspra]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodIspraMapboxStyle,
    legend: createLegendComponent(InlandFloodIspraRiskLegend),
  },

  [HazardIdEnum.SeismicClassificationIta]: {
    mapStyle: riskEvaluationMapStyles.seismicClassificationITARiskStyle,
    legend: createLegendComponent(SeismicClassificationITALegend),
  },

  // New , 2025 version for Europe

  [HazardIdEnum.InlandFloodEur10y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR10YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 10,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur20y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR20YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 20,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur30y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR30YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 30,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur40y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR40YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 40,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur50y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR50YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 50,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur75y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR75YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 75,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur100y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR100YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 100,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur200y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR200YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 200,
      copyright: "2025",
      country: "EUR",
    }),
  },
  [HazardIdEnum.InlandFloodEur500y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodEUR500YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 500,
      copyright: "2025",
      country: "EUR",
    }),
  },

  // New , 2025 version for Italy

  [HazardIdEnum.InlandFloodIta10y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA10YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 10,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta20y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA20YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 20,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta30y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA30YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 30,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta40y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA40YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 40,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta50y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA50YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 50,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta75y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA75YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 75,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta100y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA100YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 100,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta200y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA200YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 200,
      copyright: "2025",
      country: "ITA",
    }),
  },
  [HazardIdEnum.InlandFloodIta500y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodITA500YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 500,
      copyright: "2025",
      country: "ITA",
    }),
  },

  // New , 2025 version for Usa

  [HazardIdEnum.InlandFloodUsa10y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA10YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 10,
      copyright: "2025",
      country: "USA",
    }),
  },
  [HazardIdEnum.InlandFloodUsa20y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA20YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 20,
      copyright: "2025",
      country: "USA",
    }),
  },

  [HazardIdEnum.InlandFloodUsa50y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA50YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 50,
      copyright: "2025",
      country: "USA",
    }),
  },
  [HazardIdEnum.InlandFloodUsa75y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA75YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 75,
      copyright: "2025",
      country: "USA",
    }),
  },
  [HazardIdEnum.InlandFloodUsa100y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA100YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 100,
      copyright: "2025",
      country: "USA",
    }),
  },
  [HazardIdEnum.InlandFloodUsa200y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA200YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 200,
      copyright: "2025",
      country: "USA",
    }),
  },
  [HazardIdEnum.InlandFloodUsa500y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodUSA500YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 500,
      copyright: "2025",
      country: "USA",
    }),
  },

  // New , 2025 version for Bra (Brazil)

  [HazardIdEnum.InlandFloodBra10y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA10YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 10,
      copyright: "2025",
      country: "BRA",
    }),
  },
  [HazardIdEnum.InlandFloodBra20y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA20YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 20,
      copyright: "2025",
      country: "BRA",
    }),
  },

  [HazardIdEnum.InlandFloodBra50y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA50YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 50,
      copyright: "2025",
      country: "BRA",
    }),
  },
  [HazardIdEnum.InlandFloodBra75y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA75YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 75,
      copyright: "2025",
      country: "BRA",
    }),
  },
  [HazardIdEnum.InlandFloodBra100y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA100YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 100,
      copyright: "2025",
      country: "BRA",
    }),
  },
  [HazardIdEnum.InlandFloodBra200y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA200YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 200,
      copyright: "2025",
      country: "BRA",
    }),
  },
  [HazardIdEnum.InlandFloodBra500y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodBRA500YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 500,
      copyright: "2025",
      country: "BRA",
    }),
  },

  // Global

  [HazardIdEnum.InlandFloodGlobal10y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodGlobal10YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 10,
      copyright: "1995-2021",
      country: "Global",
    }),
  },
  [HazardIdEnum.InlandFloodGlobal20y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodGlobal20YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 20,
      copyright: "1995-2021",
      country: "Global",
    }),
  },
  [HazardIdEnum.InlandFloodGlobal50y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodGlobal50YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 50,
      copyright: "1995-2021",
      country: "Global",
    }),
  },
  [HazardIdEnum.InlandFloodGlobal100y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodGlobal100YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 100,
      copyright: "1995-2021",
      country: "Global",
    }),
  },
  [HazardIdEnum.InlandFloodGlobal500y]: {
    mapStyle: riskEvaluationMapStyles.inlandFloodGlobal500YearsMapBoxStyle,
    legend: createLegendComponent(InlandFloodEULegend, {
      periodOfTime: 500,
      copyright: "1995-2021",
      country: "Global",
    }),
  },

  [HazardIdEnum.CoastalFlood10y]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodMapStyle10,
    legend: createLegendComponent(CoastalFloodEULegend, { periodOfTime: 10 }),
  },
  [HazardIdEnum.CoastalFlood30y]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodMapStyle30,
    legend: createLegendComponent(CoastalFloodEULegend, { periodOfTime: 30 }),
  },
  [HazardIdEnum.CoastalFlood100y]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodMapStyle100,
    legend: createLegendComponent(CoastalFloodEULegend, { periodOfTime: 100 }),
  },
  [HazardIdEnum.CoastalFlood300y]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodMapStyle300,
    legend: createLegendComponent(CoastalFloodEULegend, { periodOfTime: 300 }),
  },
  [HazardIdEnum.CoastalFlood1000y]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodMapStyle1000,
    legend: createLegendComponent(CoastalFloodEULegend, { periodOfTime: 1000 }),
  },

  [HazardIdEnum.CoastalFloodEurProt]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodProtectionMapStyle,
    legend: createLegendComponent(CoastalFloodLegend),
  },
  [HazardIdEnum.CoastalFloodEurNoProt]: {
    mapStyle: riskEvaluationMapStyles.coastalFloodNoProtectionMapStyle,
    legend: createLegendComponent(CoastalFloodLegend, { isTrue: "No" }),
  },

  [HazardIdEnum.FlashFloodIta3h]: {
    mapStyle: riskEvaluationMapStyles.flashFlood3hMapboxStyle,
    legend: createLegendComponent(FlashFlood3hEULegend),
  },
  [HazardIdEnum.FlashFloodIta24h]: {
    mapStyle: riskEvaluationMapStyles.flashFlood24hMapboxStyle,
    legend: createLegendComponent(FlashFlood24hEULegend),
  },
  [HazardIdEnum.FlashFloodIta48h]: {
    mapStyle: riskEvaluationMapStyles.flashFlood48hMapboxStyle,
    legend: createLegendComponent(FlashFlood48hEULegend),
  },
  [HazardIdEnum.FlashFloodIta72h]: {
    mapStyle: riskEvaluationMapStyles.flashFlood72hMapboxStyle,
    legend: createLegendComponent(FlashFlood72hEULegend),
  },

  [HazardIdEnum.HailItaLarge]: {
    mapStyle: riskEvaluationMapStyles.largeHailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITALargeLegend),
  },
  [HazardIdEnum.HailItaLargeRcp45]: {
    mapStyle: riskEvaluationMapStyles.largeRCP45HailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITALargeLegend),
  },
  [HazardIdEnum.HailItaLargeRcp85]: {
    mapStyle: riskEvaluationMapStyles.largeRCP85HailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITALargeLegend),
  },

  [HazardIdEnum.HailItaExLarge]: {
    mapStyle: riskEvaluationMapStyles.exLargeHailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITAEXLargeLegend),
  },
  [HazardIdEnum.HailItaExLargeRcp45]: {
    mapStyle: riskEvaluationMapStyles.exLargeRCP45HailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITAEXLargeLegend),
  },
  [HazardIdEnum.HailItaExLargeRcp85]: {
    mapStyle: riskEvaluationMapStyles.exLargeRCP85HailRiskITAMapboxStyle,
    legend: createLegendComponent(HailITAEXLargeLegend),
  },

  [HazardIdEnum.ExplosionIta]: {
    sourceLayer: explosionLayer as LayerProps,
    getSourceData: createExplosionRiskCircles as () => Promise<GeoJsonData>,
    // Todo: centers should also be passed. May be  sourceLayerExtra and getSourceDataExtra ?
    sourceLayerSecondary: explosionCentersLayer as LayerProps,
    getSourceDataSecondary: createExplosionRiskCenters as () => Promise<GeoJsonData>,

    legend: createLegendComponent(ExplosionLegend),
  },

  [HazardIdEnum.HighWindItaSevere]: {
    mapStyle: riskEvaluationMapStyles.highWindITASevereMapboxStyle,
    legend: createLegendComponent(HighWindSevereITALegend),
  },
  [HazardIdEnum.HighWindItaSevereRcp45]: {
    mapStyle: riskEvaluationMapStyles.highWindITASevereRCP45MapboxStyle,
    legend: createLegendComponent(HighWindSevereITALegend),
  },
  [HazardIdEnum.HighWindItaSevereRcp85]: {
    mapStyle: riskEvaluationMapStyles.highWindITASevereRCP85MapboxStyle,
    legend: createLegendComponent(HighWindSevereITALegend),
  },

  [HazardIdEnum.HighWindItaExtreme]: {
    mapStyle: riskEvaluationMapStyles.highWindITAExtremeMapboxStyle,
    legend: createLegendComponent(HighWindExtremeITALegend),
  },
  [HazardIdEnum.HighWindItaExtremeRcp45]: {
    mapStyle: riskEvaluationMapStyles.highWindITAExtremeRCP45MapboxStyle,
    legend: createLegendComponent(HighWindExtremeITALegend),
  },
  [HazardIdEnum.HighWindItaExtremeRcp85]: {
    mapStyle: riskEvaluationMapStyles.highWindITAExtremeRCP85MapboxStyle,
    legend: createLegendComponent(HighWindExtremeITALegend),
  },

  [HazardIdEnum.SnowIta5cm]: {
    mapStyle: riskEvaluationMapStyles.snow5CMMapboxStyle,
    legend: createLegendComponent(Snow5CMLegend),
  },
  [HazardIdEnum.SnowIta25cm]: {
    mapStyle: riskEvaluationMapStyles.snow25CMMapboxStyle,
    legend: createLegendComponent(Snow25CMLegend),
  },

  [HazardIdEnum.LightningStrikeIta]: {
    mapStyle: riskEvaluationMapStyles.lightningStrikeMapboxStyle,
    legend: createLegendComponent(LightningStrikeLegend),
  },

  [HazardIdEnum.WindItaANIA]: {
    sourceLayer: ItaAniaWindLevelLayer(ANIA_ENUM.ANIA_01),
    getSourceData: getAniaMap,
    legend: createLegendComponent(ItaAniaWindLegend, { aniaEnum: ANIA_ENUM.ANIA_01 }),
  },
  [HazardIdEnum.HailItaANIA]: {
    sourceLayer: ItaAniaWindLevelLayer(ANIA_ENUM.ANIA_02),
    getSourceData: getAniaMap,
    legend: createLegendComponent(ItaAniaWindLegend, { aniaEnum: ANIA_ENUM.ANIA_02 }),
  },
  [HazardIdEnum.LightningStrikeItaANIA]: {
    sourceLayer: ItaAniaWindLevelLayer(ANIA_ENUM.ANIA_03),
    getSourceData: getAniaMap,
    legend: createLegendComponent(ItaAniaWindLegend, { aniaEnum: ANIA_ENUM.ANIA_03 }),
  },
  [HazardIdEnum.PrecipitationIta75mmANIA]: {
    sourceLayer: ItaAniaWindLevelLayer(ANIA_ENUM.ANIA_04),
    getSourceData: getAniaMap,
    legend: createLegendComponent(ItaAniaWindLegend, { aniaEnum: ANIA_ENUM.ANIA_04 }),
  },
  [HazardIdEnum.SnowIta30cmANIA]: {
    sourceLayer: ItaAniaWindLevelLayer(ANIA_ENUM.ANIA_05),
    getSourceData: getAniaMap,
    legend: createLegendComponent(ItaAniaWindLegend, { aniaEnum: ANIA_ENUM.ANIA_05 }),
  },

  [HazardIdEnum.WarningLandslide]: {
    sourceLayer: landslideLayer,
    getSourceData: getGlobalLandslideNowcastGeoJsonUrlForItaly,
    legend: createLegendComponent(LandslideLegend),
  },

  [HazardIdEnum.WarningRainfall1Day]: {
    sourceLayer: rainfallLevelLayer,
    getSourceData: getRainfallOneDayGeoJSONUrlForItaly,
    legend: createLegendComponent(Rainfall1daysLegend),
  },

  [HazardIdEnum.WarningRainfall7Day]: {
    sourceLayer: rainfallLevelLayer,
    getSourceData: getRainfallSevenDayGeoJSONUrlForItaly,
    legend: createLegendComponent(Rainfall7daysLegend),
  },

  [HazardIdEnum.EarthquakeMagnitude]: {
    sourceLayer: heatmapLayer,
    getSourceData: getEarthquakeMagnitudeForItaly,
    legend: createLegendComponent(EarthquakeMagnitudeLegend),
  },
  [HazardIdEnum.RealeGroupHail]: {
    mapStyle: riskEvaluationMapStyles.realeGroupHail,
    legend: createLegendComponent(HailsRmaLegend),
  },

  [HazardIdEnum.RealeGroupWind]: {
    mapStyle: riskEvaluationMapStyles.realeGroupWind,
    legend: createLegendComponent(WindyRmaLegend),
  },

  // COCO VERSION 1

  ...makePairOfCoCoHazards(
    HazardIdEnum.SeismicCoCoIta,
    HazardIdEnum.SeismicCoCoRma,
    cocoLayerSeismicCCT,
    "Seismic",
    "cocoT",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.FloodCoCoIta,
    HazardIdEnum.FloodCoCoRma,
    cocoLayerFloodCCA,
    "Flood",
    "cocoA",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.LandslideCoCoIta,
    HazardIdEnum.LandslideCoCoRma,
    cocoLayerLandslideCCF,
    "Landslide",
    "cocoF",
  ),

  // COCO VERSION 2

  ...makePairOfCoCoHazards(
    HazardIdEnum.SeismicCoCo2Ita,
    HazardIdEnum.SeismicCoCo2Rma,
    cocoLayerSeismicCCT,
    "Seismic",
    "cocoT2",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.FloodCoCo2Ita,
    HazardIdEnum.FloodCoCo2Rma,
    cocoLayerFloodCCA,
    "Flood",
    "cocoA2",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.LandslideCoCo2Ita,
    HazardIdEnum.LandslideCoCo2Rma,
    cocoLayerLandslideCCF,
    "Landslide",
    "cocoF2",
  ),

  // COCO VERSION 3

  ...makePairOfCoCoHazards(
    HazardIdEnum.SeismicCoCo3Ita,
    HazardIdEnum.SeismicCoCo3Rma,
    cocoLayerSeismicCCT,
    "Seismic",
    "cocoT3",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.FloodCoCo3Ita,
    HazardIdEnum.FloodCoCo3Rma,
    cocoLayerFloodCCA,
    "Flood",
    "cocoA3",
  ),
  ...makePairOfCoCoHazards(
    HazardIdEnum.LandslideCoCo3Ita,
    HazardIdEnum.LandslideCoCo3Rma,
    cocoLayerLandslideCCF,
    "Landslide",
    "cocoF3",
  ),

  // COCO VERSION 4
  ...makePairOfCoCoHazards(
    HazardIdEnum.SeismicCoCo4Ita,
    HazardIdEnum.SeismicCoCo4Rma,
    cocoLayerSeismicCCT,
    "Seismic",
    "cocoT4",
  ),

  // PRC

  [HazardIdEnum.LandslideTodayPrc]: {
    sourceLayer: proCivileLayer("Idrogeo"),
    getSourceData: getProCivileTodayMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "landslide", day: "today" }),
  },
  [HazardIdEnum.ThunderstormTodayPrc]: {
    sourceLayer: proCivileLayer("Temporali"),
    getSourceData: getProCivileTodayMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "thunderstorm", day: "today" }),
  },
  [HazardIdEnum.FloodTodayPrc]: {
    sourceLayer: proCivileLayer("Idraulico"),
    getSourceData: getProCivileTodayMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "flood", day: "today" }),
  },
  [HazardIdEnum.LandslideTomorrowPrc]: {
    sourceLayer: proCivileLayer("Idrogeo"),
    getSourceData: getProCivileTomorrowMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "landslide", day: "tomorrow" }),
  },
  [HazardIdEnum.ThunderstormTomorrowPrc]: {
    sourceLayer: proCivileLayer("Temporali"),
    getSourceData: getProCivileTomorrowMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "thunderstorm", day: "tomorrow" }),
  },
  [HazardIdEnum.FloodTomorrowPrc]: {
    sourceLayer: proCivileLayer("Idraulico"),
    getSourceData: getProCivileTomorrowMap,
    legend: createLegendComponent(ProtezioneCivileLegend, { hazardName: "flood", day: "tomorrow" }),
  },

  [HazardIdEnum.SeismicRiskHowden2]: {
    mapStyle: riskEvaluationMapStyles.seismicNatcat2RiskStyle,
    legend: createLegendComponent(SeismicNatcatLegend, { howdenVersion: 2 }),
  },

  [HazardIdEnum.SeismicRiskGemIsl]: {
    mapStyle: riskEvaluationMapStyles.seismicRiskGemIsl,
    legend: createLegendComponent(SeismicIslNatcatLegend),
  },
  [HazardIdEnum.SeismicRiskGemGlobal]: {
    mapStyle: riskEvaluationMapStyles.seismicRiskGemGlobal,
    legend: createLegendComponent(SeismicIslNatcatLegend),
  },
  [HazardIdEnum.StormSurgeCat1Us]: {
    mapStyle: riskEvaluationMapStyles.stormSurgeCategory1MapStyle,
    legend: createLegendComponent(StormSurgeUSLegend),
  },
  [HazardIdEnum.WildfireVHPIndexUs]: {
    mapStyle: riskEvaluationMapStyles.wildfireUSVHPIndexMapStyle,
    legend: createLegendComponent(WildfireVHPIndexUsLegend),
  },

  [HazardIdEnum.EnvironmentDistanceFireStation]: {
    sourceLayer: environmentDistanceFacilityLayer,
    getSourceDataWithParams: getEnvironmentDistanceFireStationData,
    legendWithParams: EnvironmentDistanceFireStationLegend,
  },
  [HazardIdEnum.EnvironmentDistanceSchool]: {
    sourceLayer: environmentDistanceFacilityLayer,
    getSourceDataWithParams: getEnvironmentDistanceSchoolData,
    legendWithParams: EnvironmentDistanceSchoolLegend,
  },

  [HazardIdEnum.LandCoverCropTypeEur2023]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    sourceLayer: genericRasterLayer(),
    sourceTileUrl: landCoverWrlTileUrl("HRL_CPL:CTY_S2023"),
    legend: createLegendComponent(WmsGetLegendGraphic, {
      legendAsJson: CLMS_HRL_CropTypes_S2023_R10m_json,
      description: landCoverGenericTypeDescription("Crop"),
    }),
  },

  [HazardIdEnum.LandCoverForestTypeEur2023]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    sourceLayer: genericRasterLayer(),
    sourceTileUrl: landCoverWrlTileUrl("HRL_TCF:DLT_S2023"),
    legend: createLegendComponent(WmsGetLegendGraphic, {
      legendAsJson: CLMS_HRL_ForestTypes_S2023_R10m_json,
      description: landCoverGenericTypeDescription("Forest"),
    }),
  },

  [HazardIdEnum.WorldCover2021]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    sourceLayer: genericRasterLayer(),
    sourceTileUrl: worldCoverWrlTileUrl(),
    legend: createLegendComponent(WmsGetLegendGraphic, {
      legendAsJson: ESA_WorldCover_2021_v2_json,
      description: worldCoverTypeDescription(),
    }),
  },

  // Digital elevation, 4 maps:

  [HazardIdEnum.DigitalElevationModelSwe]: {
    mapStyle: riskEvaluationMapStyles.digitalElevationModelSweMapStyle,
    legend: createLegendComponent(DigitalElevationModelLegend),
  },
  [HazardIdEnum.DigitalElevationModelIta]: {
    mapStyle: riskEvaluationMapStyles.digitalElevationModelItaMapStyle,
    legend: createLegendComponent(DigitalElevationModelLegend),
  },

  [HazardIdEnum.HeightAboveNearestDrainageIta]: {
    mapStyle: riskEvaluationMapStyles.heightAboveNearestDrainageItaMapStyle,
    legend: createLegendComponent(HeightAboveNearestDrainageLegend),
  },
  [HazardIdEnum.HeightAboveNearestDrainageSwe]: {
    mapStyle: riskEvaluationMapStyles.heightAboveNearestDrainageSweMapStyle,
    legend: createLegendComponent(HeightAboveNearestDrainageLegend),
  },

  // Population density, 3 maps:

  [HazardIdEnum.PopulationDensityEurStat22021]: {
    mapStyle: riskEvaluationMapStyles.populationDensityEurStat22021MapStyle,
    legend: createLegendComponent(PopulationDensityEurLegend),
  },
  [HazardIdEnum.PopulationDensityWorld]: {
    mapStyle: riskEvaluationMapStyles.populationDensityWorldMapStyle,
    legend: createLegendComponent(PopulationDensityWorldLegend),
  },
  [HazardIdEnum.PopulationDensityUsa]: {
    mapStyle: riskEvaluationMapStyles.populationDensityUsaMapStyle,
    legend: createLegendComponent(PopulationDensityUsaLegend),
  },

  [HazardIdEnum.DistanceToNearestCoastWorld]: {
    mapStyle: riskEvaluationMapStyles.distanceToNearestCoastWorldMapStyle,
    legend: createLegendComponent(DistanceToTheNearestCoastLegend),
  },
  [HazardIdEnum.DistanceToNearestCoastIta]: {
    mapStyle: riskEvaluationMapStyles.distanceToNearestCoastItaMapStyle,
    legend: createLegendComponent(DistanceToTheNearestCoastLegend),
  },

  [HazardIdEnum.CrimeDatasetsSwe]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    sourceLayer: crimeDatasetsSweLayer,
    getSourceData: getCrimeDataMapSwe,
    legend: createLegendComponent(CrimeDatasetSweLegend),
  },

  [HazardIdEnum.NasaCastPrecipitationIta1Day]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    getSourceData: () => getNasaCastPrecipitationItaGeoJson(1),
    sourceLayer: nasaPrecipitationColorsLayer,
    legend: createLegendComponent(NasaNowcastPrecipitationLegend, { days: 1 }),
  },
  [HazardIdEnum.NasaCastPrecipitationIta3Day]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    getSourceData: () => getNasaCastPrecipitationItaGeoJson(3),
    sourceLayer: nasaPrecipitationColorsLayer,
    legend: createLegendComponent(NasaNowcastPrecipitationLegend, { days: 3 }),
  },
  [HazardIdEnum.NasaCastPrecipitationIta7Day]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    getSourceData: () => getNasaCastPrecipitationItaGeoJson(7),
    sourceLayer: nasaPrecipitationColorsLayer,
    legend: createLegendComponent(NasaNowcastPrecipitationLegend, { days: 7 }),
  },

  [HazardIdEnum.NasaCastLandslideIta]: {
    mapStyle: "mapbox://styles/mapbox/light-v10",
    getSourceData: getNasaCastLandslideItaGeoJson,
    sourceLayer: nasaLandslideColorsLayer,
    legend: createLegendComponent(NasaNowcastLandslideLegend),
  },
};
